<template>
    <component
        :is="props.openMode === 'emit' ? 'div' : RouterLink"
        :class="{
            compact: props.listLayout === 'compact',
            data: props.isData,
            unread: props.isUnread,
            clickable: props.openMode === 'emit',
            'linkedin-post': isLinkedInPost,
        }"
        :role="props.openMode === 'emit' ? 'button' : undefined"
        :tabindex="props.openMode === 'emit' ? 0 : undefined"
        :to="props.openMode === 'emit' ? undefined : destinationRoute"
        class="list-item pdf"
        @click="handleClick"
        @keydown.enter="handleKeyboardOpen"
        @keydown.space.prevent="handleKeyboardOpen"
    >
        <div class="title">
            <div class="title-new-holder">
                <span
                    v-if="props.isUnread"
                    class="document-unread-dot"
                    aria-label="Unread"
                />
                <div class="title-text">{{ props.pdf.documentTitle }}</div>
                <div class="upcoming-document" v-if="isPublicationDateInFuture(props.pdf.publicationDate)">Upcoming</div>
                <div class="new-document" v-else-if="isNew(props.pdf.publicationDate)">New</div>
            </div>
            <div v-if="isRichContentDocument(props.pdf) && !isLinkedInPost" class="rich-tag-row">
                <primevue-tag
                    :value="contentTypeLabel"
                    severity="secondary"
                />
            </div>
            <div v-else-if="!hasTypeLogo" class="type-pill">
                {{ contentTypeLabel }}
            </div>
        </div>
        <div class="subtitle" v-if="subtitleText && props.listLayout !== 'compact' && !props.hideDescription && !isLinkedInPost">
            {{ subtitleText }}
        </div>
        <div class="date">
            <icon-with-count-badge v-if="props.pdf.commentCount" :icon="['fas', 'comment']" :count="props.pdf.commentCount ?? 0" aria-label="Comment count" />
            <document-access-tag v-if="!isRichContentDocument(props.pdf)" :access-reason="props.pdf.accessReason" />
            <span v-if="publishedDate">{{ publishedDate }}</span>
            <report-bookmark-button :report-id="props.pdf.documentShortCode" />
        </div>
        <template v-if="props.listLayout === 'full'">
            <div class="thumbnail-holder">
                <div v-if="isLinkedInPost" class="linkedin-thumbnail">
                    <font-awesome-icon :icon="['fab', 'linkedin']" />
                </div>
                <div v-else-if="isRichContentDocument(props.pdf)" class="rich-thumbnail">
                    <img
                        v-if="shouldShowRichThumbnail"
                        :src="props.pdf.thumbnailUrl"
                        alt=""
                        loading="lazy"
                        decoding="async"
                        @error="handleRichThumbnailError"
                    />
                    <font-awesome-icon
                        v-else-if="richContentFallbackIcon?.kind === 'icon'"
                        :icon="[richContentFallbackIcon.iconFamily, richContentFallbackIcon.source]"
                        :style="richContentFallbackIcon.colour ? { color: richContentFallbackIcon.colour } : undefined"
                    />
                </div>
                <report-thumbnail v-else :thumbnail-url="props.pdf.thumbnailUrl" :report-type="props.pdf.reportType" />
            </div>
            <div v-if="props.pdf.description && !isLinkedInPost" class="description" :class="{ 'list-view': !props.isData }">{{ stripHtml(props.pdf.description) }}</div>
        </template>
        <div class="view-button">
            <div v-if="showInlineTypeLogo" class="content-type-inline">
                <img
                    v-if="inlineTypeLogo?.kind === 'image'"
                    :src="inlineTypeLogo.source"
                    :alt="inlineTypeLogo.alt"
                    class="type-logo"
                />
                <font-awesome-icon
                    v-else-if="inlineTypeLogo?.kind === 'icon'"
                    :icon="[inlineTypeLogo.iconFamily, inlineTypeLogo.source]"
                    :style="inlineTypeLogo.colour ? { color: inlineTypeLogo.colour } : undefined"
                    class="type-logo"
                />
            </div>
            <div class="icon-holder" :class="{ light: uiStore.theme === 'light' }">
                <font-awesome-icon
                    :icon="['fas', 'arrow-right']"
                />
            </div>
        </div>
    </component>
</template>

<script setup lang="ts">
import { RouterLink, useRoute } from 'vue-router';
import { IDocumentSimple, IReportTypeMetadata, TReportTypeLogo } from '@/interfaces/deltaGpt/IDocument.ts';
import { stripHtml } from '@/utils/htmlUtils.ts';
import { deltaGptRoutes } from '@/routes/deltaGptRoutes.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import { getResearchSectionRouteName } from '@/routes/routeUtils.ts';
import DocumentAccessTag from '@/components/common/DocumentAccessTag.vue';
import ReportThumbnail from '@/components/sectionComponents/ReportThumbnail.vue';
import ReportBookmarkButton from '@/components/deltaGpt/ReportBookmarkButton.vue';
import { computed, ref } from 'vue';
import { getReportTypeMetadata, isLinkedInReportType, isRichContentDocument } from '@/utils/researchContent.ts';
import { formatPublicationDateForDisplay, isPublicationDateInFuture, parsePublicationDate } from '@/utils/sparkContentUtils.ts';
import PrimevueTag from 'primevue/tag';
import IconWithCountBadge from '@/components/common/IconWithCountBadge.vue';
import { getDisplayCommunityThreadPageLinks } from '@/utils/communityPageLinkRegistry.ts';

const uiStore = useUIStore();
const route = useRoute();
const richThumbnailFailed = ref<boolean>(false);

const props = defineProps<{
    pdf: IDocumentSimple;
    listLayout: string;
    hideDescription?: boolean;
    isData: boolean;
    isUnread?: boolean;
    documentRouteName?: string;
    openMode?: 'route' | 'emit';
}>();

const emit = defineEmits<{
    openDocument: [IDocumentSimple];
}>();

const publishedDate = computed<string>(() => formatPublicationDateForDisplay(props.pdf.publicationDate));

const destinationRoute = computed(() => (
    {
        name: props.documentRouteName ?? String(getResearchSectionRouteName(route, deltaGptRoutes.documentView.name) ?? ''),
        params: { documentShortCode: props.pdf.documentShortCode },
        query: route.query,
    }
));

const reportTypeMetadata = computed<IReportTypeMetadata>(() => getReportTypeMetadata(props.pdf.reportType));
const contentTypeLabel = computed<string>(() => reportTypeMetadata.value.label);
const inlineTypeLogo = computed<TReportTypeLogo | undefined>(() => reportTypeMetadata.value.logo);

const isLinkedInPost = computed<boolean>(() => isLinkedInReportType(props.pdf.reportType));
const hasTypeLogo = computed<boolean>(() => inlineTypeLogo.value !== undefined);
const showInlineTypeLogo = computed<boolean>(() => hasTypeLogo.value && !isLinkedInPost.value);

const subtitleText = computed<string>(() => {
    if (isRichContentDocument(props.pdf)) {
        return richPageLinks.value.map(pageLink => pageLink.label).join(' | ');
    }

    return props.pdf.documentSubtitle ?? '';
});

const richPageLinks = computed(() => {
    if (!isRichContentDocument(props.pdf)) return [];
    return getDisplayCommunityThreadPageLinks((props.pdf.routeNames ?? []).map(routeName => ({ routeName })));
});

const richContentFallbackIcon = computed<TReportTypeLogo | undefined>(() => (
    reportTypeMetadata.value.logo?.kind === 'icon' ? reportTypeMetadata.value.logo : undefined
));

const shouldShowRichThumbnail = computed<boolean>(() => (
    isRichContentDocument(props.pdf)
    && !!props.pdf.thumbnailUrl
    && !richThumbnailFailed.value
));

function handleRichThumbnailError (): void {
    richThumbnailFailed.value = true;
}

function handleClick (event: MouseEvent): void {
    if (props.openMode !== 'emit') return;

    event.preventDefault();
    emit('openDocument', props.pdf);
}

function handleKeyboardOpen (): void {
    if (props.openMode !== 'emit') return;

    emit('openDocument', props.pdf);
}

function isNew (date?: string | null): boolean {
    const publicationDate = parsePublicationDate(date);
    if (!publicationDate) return false;

    const thirtyDaysAgo = parsePublicationDate(new Date().toISOString())?.subtract(30, 'day');
    if (!thirtyDaysAgo) return false;
    return publicationDate.isAfter(thirtyDaysAgo) || publicationDate.isSame(thirtyDaysAgo, 'day');
}

</script>

<style lang="scss" scoped>
.pdf {
    column-gap: var(--defaultSpacing);
    display: grid;
    color: var(--primaryFont);
    padding: var(--defaultSpacing) 0;
    grid-template-columns: auto 1fr auto;
    grid-template-rows: auto auto auto;
    grid-template-areas:
        "thumbnail title date"
        "thumbnail subtitle subtitle"
        "thumbnail description view";

    @include responsive(sm) {
        grid-template-columns: 1fr 1fr auto;
        grid-template-rows: auto auto 1fr;
        grid-template-areas:
            "thumbnail title title"
            "thumbnail subtitle subtitle"
            "thumbnail date date"
            "description description view";
        gap: var(--smallSpacing);
        padding: var(--defaultSpacing);
        margin-bottom: var(--defaultSpacing);
        background-color: var(--neutral100);
    }

    &.data {
        padding: var(--largeSpacing);
        grid-template-columns: auto 1fr auto;
        grid-template-rows: auto auto auto;
        grid-template-areas:
            "thumbnail title title"
            "thumbnail description description"
            "date date view";
        background-color: var(--neutral100);
        border-radius: var(--borderRadiusOuter);
        height: 25rem;
        gap: 0 var(--defaultSpacing);

        @include responsive(md) {
            height: auto;
            padding: var(--defaultSpacing);

            .view-button {
                padding-right: 0;
            }
            .title{
                align-items: normal;
                .new-document, .upcoming-document {
                    align-self: baseline;
                }
            }

            .description {
                $numberOfLines: 6;
                font-size: var(--extraSmallFontSize);
                line-height: var(--largeSpacing);
                height: calc(var(--largeSpacing) * $numberOfLines);
                -webkit-line-clamp: $numberOfLines; /* Number of visible lines */
                line-clamp: $numberOfLines;
                text-overflow: ellipsis;
            }

            &:hover {
                transform: scale(1.01);
                transition: transform 0.4s ease;
            }
        }

        .view-button {
            padding-right: 0;
        }
        .title{
            align-items: normal;
            .new-document, .upcoming-document {
                align-self: baseline;
            }
        }
        .description {
            $numberOfLines: 6;
            font-size: var(--extraSmallFontSize);
            line-height: var(--largeSpacing);
            height: calc(var(--largeSpacing) * $numberOfLines);
            display: -webkit-box;
            -webkit-box-orient: vertical;
            overflow: hidden;
            -webkit-line-clamp: $numberOfLines;
            line-clamp: $numberOfLines;
            text-overflow: ellipsis;
        }
        &:hover {
            transform: scale(1.01);
            transition: transform 0.4s ease;
        }    }

    &:hover {
        .icon-holder {
            background-color: var(--neutral400) !important;
            transform: scale(1.05);
            transition: transform 0.2s ease, background-color 0.2s ease;

        }
        .title-text,
        .new-document,
        .upcoming-document {
            transform: scale(1.02);
            transition: transform 0.2s ease, background-color 0.2s ease;
            transform-origin: left center;
        }
    }
    &.linkedin-post {
        align-items: center;
        grid-template-rows: auto;
        grid-template-areas: "thumbnail title date view";
        padding: var(--largeSpacing) 0;

        .title,
        .date,
        .view-button,
        .thumbnail-holder {
            align-self: center;
        }

        @include responsive(sm) {
            grid-template-columns: auto 1fr auto;
            grid-template-areas:
                "thumbnail title view"
                "thumbnail date date";
            padding: var(--defaultSpacing);

            .view-button {
                padding-right: 0;
            }
        }
    }
    .thumbnail-holder {
        grid-area: thumbnail;
    }

    &.unread {
        .title-text {
            font-weight: var(--extraBoldFontWeight);
        }
    }

    &.clickable {
        cursor: pointer;
    }

    .date {
        align-items: center;
        display: flex;
        font-size: var(--smallFontSize);
        grid-area: date;
        gap: var(--defaultSpacing);
        padding: 0 var(--defaultSpacing);
        width: max-content;
        justify-self: end;
    }

    &.compact {
        align-items: center;
        justify-items: center;
        min-height: initial;
        grid-template-columns: auto 1fr auto;
        grid-template-rows: auto;
        grid-template-areas: "date title view";
        padding: var(--defaultSpacing);
        gap: var(--defaultSpacing);

        .date {
            justify-content: flex-start;

            @include responsive(md) {
                display: none;
            }
        }
    }

    .pdf-img {
        grid-area: icon;
        height: 3rem;
        width: 3rem;
        font-size: 2.5rem;
        margin: var(--defaultSpacing) 0 0 var(--defaultSpacing);
    }

    .title {
        gap: var(--defaultSpacing);
        grid-area: title;
        white-space: wrap;
        text-overflow: ellipsis;
        display: flex;
        align-items: flex-start;
        margin-right: auto;
        width: 100%;

        @include responsive(md) {
            font-size: var(--defaultFontSize);
        }

        .title-new-holder {
            align-items: center;
            display: flex;
            flex: 1 1 auto;
            gap: var(--defaultSpacing);
            min-width: 0;

            .document-unread-dot {
                background-color: var(--accentColour);
                border-radius: 50%;
                flex: 0 0 auto;
                height: 0.65rem;
                width: 0.65rem;
            }

            .new-document, .upcoming-document {
                border-radius: var(--borderRadiusOuter);
                font-size: var(--extraSmallFontSize);
                padding: var(--extraSmallSpacing) var(--smallSpacing);
                align-self: center;
            }

            .new-document {
                background-color: var(--lcpMoss);
            }

            .upcoming-document {
                background-color: var(--accentColour);
            }
        }

        .type-pill {
            font-size: var(--extraSmallFontSize);
            font-weight: var(--boldFontWeight);
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--accentColour);
            background: var(--neutral200);
            border-radius: var(--borderRadiusOuter);
            padding: var(--extraSmallSpacing) var(--smallSpacing);
            margin-left: auto;
            flex-shrink: 0;
        }
    }

    .rich-tag-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--smallSpacing);
        justify-content: flex-end;
    }
    .linkedin-thumbnail {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--borderRadiusOuter);
        color: var(--actionColour);
        font-size: var(--extraLargeFontSize);
    }

    .rich-thumbnail {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18rem;
        height: 10rem;
        border: 1px solid var(--neutral300);
        border-radius: var(--borderRadiusOuter);
        background: var(--neutral200);
        color: var(--accentColour);
        font-size: var(--extraLargeFontSize);
        overflow: hidden;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: top center;
        }

        @include responsive(sm) {
            width: 100%;
        }
    }

    .subtitle {
        grid-area: subtitle;
        font-size: var(--smallFontSize);
        color: var(--neutral700);
        font-style: italic;
        margin: 0 var(--defaultSpacing) var(--smallSpacing) 0;
        padding-bottom: var(--extraSmallSpacing);
    }

    .description {
        $numberOfLines: 3;
        display: -webkit-box;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: var(--smallFontSize);
        grid-area: description;
        color: var(--neutral900);
        line-height: var(--extraLargeSpacing);
        -webkit-line-clamp: $numberOfLines;
        -webkit-box-orient: vertical;
        line-clamp: $numberOfLines;
        margin: 0;

        &.list-view {
            line-height: var(--extraLargeSpacing);
        }
    }

    .view-button {
        grid-area: view;
        display: flex;
        gap: var(--defaultSpacing);
        justify-content: end;
        align-items: center;

        @include responsive(md) {
            justify-content: center;
            padding-right: 0;
            margin-top: var(--smallSpacing);
        }

        .content-type-inline {
            width: 2.75rem;
            min-width: 2.75rem;
            display: flex;
            align-items: center;
            justify-content: center;

            .type-logo {
                height: 2.5rem;
                width: auto;
                max-width: 100%;
                opacity: 0.8;
            }
        }

        .icon-holder {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 3.5rem;
            height: 3.5rem;
            font-size: 1.5rem;
            background-color: var(--neutral300);
            border-radius: 50%;

            svg {
                color: var(--actionColour);
            }
        }
    }
}

</style>
